import { useQuery } from '@tanstack/react-query';

import { managementGuidelinesApi } from '../api/management-guidelines.adapter';
import { treeToTopicNodes } from '../utils/topic-tree';

export const managementGuidelineQueryKeys = {
  all: ['management-guidelines'] as const,
  list: () => [...managementGuidelineQueryKeys.all, 'list'] as const,
  detail: (gid: string) => [...managementGuidelineQueryKeys.all, 'detail', gid] as const,
  // One key for the whole PRO tree, so React Query fetches /admin/pro/tree once
  // per session and every consumer of the cascade serves from that one cache.
  proTree: () => [...managementGuidelineQueryKeys.all, 'pro-tree'] as const,
};

// PRD §3.1 — full list, unpaginated. Search/sort run client-side (see
// management-guidelines-page.store + filter-sort util).
export function useGuidelinesQuery() {
  return useQuery({
    queryKey: managementGuidelineQueryKeys.list(),
    queryFn: () => managementGuidelinesApi.list(),
  });
}

// PRD §3.2 — detail fetch when the edit panel opens. `gid` is null in create
// mode and the query stays disabled so we don't hit the adapter with a
// sentinel.
export function useGuidelineDetailQuery(gid: string | null) {
  return useQuery({
    queryKey: managementGuidelineQueryKeys.detail(gid ?? ''),
    queryFn: () => managementGuidelinesApi.get(gid as string),
    enabled: gid !== null,
  });
}

// PRD §3.2 — the PRO tree powers the Area/Sub-area/Item cascade. We read the
// single whole-tree endpoint and `select`-transform it into cascade nodes. The
// tree is effectively static during a session, so cache it indefinitely
// (staleTime = Infinity) to avoid refetching every time the panel opens.
export function useTopicsQuery() {
  return useQuery({
    queryKey: managementGuidelineQueryKeys.proTree(),
    queryFn: () => managementGuidelinesApi.getProTree(),
    staleTime: Infinity,
    select: treeToTopicNodes,
  });
}

// SHCT-639 — `usePidRefsQuery` removed with the PID auto-link field
// (Requirement Clarification No.45 / PRD 07 v1.7).
